import { ArrowRight, CircleCheckBig } from "lucide-react";
import { Link } from "react-router-dom";
import UserAvatar from "@/components/common/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import EmptyState from "@/components/common/EmptyState";
import { formatUtcDate } from "@/utils/common.utils";
import { ATTENTION_HEALTH_DOTS } from "@/constant/overview.constant";
import type { AttentionItem } from "@/types/overview.types";

const HEALTH_DOTS = ATTENTION_HEALTH_DOTS as Record<string, string>;

interface RequiresAttentionProps {
  /** Already filtered, ranked worst-first, and sliced by the server. */
  items: AttentionItem[];
  /** Total across every page, which is what the count badge should show — the
   *  card renders only the first handful. */
  total: number;
  /** Shown instead of a dead-end empty state when nothing is flagged. An
   *  agent with a healthy pipeline should still see their pipeline. */
  activeDeals?: Array<Record<string, any>>;
  activeTotal?: number;
}

export default function RequiresAttention({
  items, total, activeDeals = [], activeTotal = 0,
}: RequiresAttentionProps) {
  // Nothing flagged *and* nothing open is the only genuine empty state.
  const showPipeline = items.length === 0 && activeDeals.length > 0;
  return (
    <Card className="flex h-[460px] flex-col overflow-hidden rounded-2xl border-dash-border shadow-none" data-testid="dashboard-requires-attention">
      <CardHeader className="flex-row items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="size-2.5 rounded-full bg-dash-slate" aria-hidden="true" />
          <CardTitle className="text-sm text-dash-ink">Requires attention</CardTitle>
          <Badge variant="outline" className="rounded-full border-transparent bg-dash-slate/10 text-dash-slate">{total}</Badge>
        </div>
        <Button variant="ghost" size="sm" className="hover:bg-dash-searchBg hover:text-dash-ink" asChild>
          <Link to="/dashboard"><span>View all</span><ArrowRight data-icon="inline-end" /></Link>
        </Button>
      </CardHeader>
      <Separator className="bg-dash-border" />
      <CardContent className="min-h-0 flex-1 overflow-y-auto p-0">
        {showPipeline ? (
          <>
            <div className="flex items-center gap-2 border-b border-dash-borderSoft bg-dash-page px-5 py-3">
              <CircleCheckBig className="size-4 shrink-0 text-dash-greenMid" aria-hidden="true" />
              <p className="text-xs text-dash-muted">
                Everything is on track — {activeTotal} active deal{activeTotal === 1 ? "" : "s"} in flight.
              </p>
            </div>
            {activeDeals.map((deal, index) => (
              <div key={deal.id}>
                {index > 0 && <Separator className="bg-dash-borderSoft" />}
                <Link to={`/deals/${deal.id}`} className="flex min-h-[66px] items-center gap-3 px-5 py-3 transition-colors hover:bg-dash-searchBg" data-testid="dashboard-active-deal">
                  <span className={`size-2 shrink-0 rounded-full ${HEALTH_DOTS[deal.deal_health] || "bg-dash-greenMid"}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-dash-ink">{deal.address}</p>
                    <p className="truncate text-xs text-dash-muted">{deal.client_name}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <UserAvatar name={deal.agent_name} src={deal.agent_photo_url} className="size-8" fallbackClassName="border border-primary" />
                    <span className="hidden w-14 text-right text-xs text-dash-faint sm:inline">
                      {deal.close_date ? formatUtcDate(deal.close_date) : "No date"}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </>
        ) : items.length === 0 ? (
          <EmptyState compact icon={CircleCheckBig} title="No deals yet" description="Open your first deal and it will show up here." className="h-full justify-center rounded-none border-0" />
        ) : items.map((item, index) => (
          <div key={item.deal_id}>
            {index > 0 && <Separator className="bg-dash-borderSoft" />}
            <Link to={`/deals/${item.deal_id}`} className="flex min-h-[66px] items-center gap-3 px-5 py-3 transition-colors hover:bg-dash-searchBg">
              <span className={`size-2 shrink-0 rounded-full ${HEALTH_DOTS[item.health] || "bg-dash-slate"}`} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-dash-ink">{item.address}</p>
                <p className="truncate text-xs text-dash-muted">{item.reason}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <UserAvatar name={item.agent_name} src={item.agent_photo_url} className="size-8" fallbackClassName="border border-primary" />
                <span className="hidden w-14 text-right text-xs text-dash-faint sm:inline">
                  {item.closing_date ? formatUtcDate(item.closing_date) : "No date"}
                </span>
              </div>
            </Link>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
